/**
 * Date and formatting utility functions for calendar operations
 */

/**
 * Format a date to YYYY-MM-DD string
 * @param date - Date object to format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export const formatDateToString = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Parse a date string in YYYY-MM-DD format to a Date object
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object
 */
export const parseStringToDate = (dateString: string): Date => {
  return new Date(dateString);
};

/**
 * Get the first day of a month
 * @param year - Year
 * @param month - Month (0-11)
 * @returns Date object representing the first day of the month
 */
export const getFirstDayOfMonth = (year: number, month: number): Date => {
  return new Date(year, month, 1);
};

/**
 * Get the last day of a month
 * @param year - Year
 * @param month - Month (0-11)
 * @returns Date object representing the last day of the month
 */
export const getLastDayOfMonth = (year: number, month: number): Date => {
  return new Date(year, month + 1, 0);
};

/**
 * Get an array of dates for a month including padding days from previous/next months
 * @param year - Year
 * @param month - Month (0-11)
 * @returns Array of date objects for the calendar grid
 */
export const getCalendarDaysArray = (year: number, month: number): Date[] => {
  const result: Date[] = [];
  
  // Get first day of the month
  const firstDay = getFirstDayOfMonth(year, month);
  
  // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = firstDay.getDay();
  
  // Add days from previous month to pad the beginning of the calendar
  const prevMonthLastDay = getLastDayOfMonth(year, month - 1).getDate();
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    result.push(new Date(year, month - 1, prevMonthLastDay - i));
  }
  
  // Add all days of the current month
  const lastDay = getLastDayOfMonth(year, month);
  for (let i = 1; i <= lastDay.getDate(); i++) {
    result.push(new Date(year, month, i));
  }
  
  // Calculate how many days from next month we need to complete the grid
  // We want to make sure we have complete weeks (multiples of 7)
  const remainingDays = 7 - (result.length % 7);
  if (remainingDays < 7) {
    for (let i = 1; i <= remainingDays; i++) {
      result.push(new Date(year, month + 1, i));
    }
  }
  
  return result;
};

/**
 * Check if a date is between two other dates (inclusive)
 * @param date - Date to check
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Boolean indicating if date is between start and end dates
 */
export const isDateBetween = (date: Date, startDate: Date, endDate: Date): boolean => {
  const d = date.getTime();
  const start = startDate.getTime();
  const end = endDate.getTime();
  return d >= start && d <= end;
};

/**
 * Calculate the number of days between two dates
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Number of days between the dates
 */
export const getDaysBetweenDates = (startDate: Date, endDate: Date): number => {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  
  // Calculate the difference in milliseconds
  const diffTime = Math.abs(end.getTime() - start.getTime());
  
  // Convert to days and add 1 to include both start and end dates
  return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

/**
 * Get an array of date strings between two dates (inclusive)
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Array of date strings in YYYY-MM-DD format
 */
export const getDateRangeArray = (startDate: Date, endDate: Date): string[] => {
  const dateArray: string[] = [];
  const currentDate = new Date(startDate);
  
  // Set time to beginning of day to avoid time zone issues
  currentDate.setHours(0, 0, 0, 0);
  const endDateCopy = new Date(endDate);
  endDateCopy.setHours(0, 0, 0, 0);
  
  // Loop through dates and add to array
  while (currentDate <= endDateCopy) {
    dateArray.push(formatDateToString(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dateArray;
};

/**
 * Format a date for display in the UI
 * @param date - Date to format
 * @param format - Format type ('short', 'medium', 'long')
 * @returns Formatted date string
 */
export const formatDateForDisplay = (date: Date | string, format: 'short' | 'medium' | 'long' = 'medium'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  switch (format) {
    case 'short':
      return dateObj.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' });
    case 'long':
      return dateObj.toLocaleDateString('en-MY', { 
        weekday: 'long',
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    case 'medium':
    default:
      return dateObj.toLocaleDateString('en-MY', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
  }
};

/**
 * Get the day name from a date
 * @param date - Date object or date string
 * @param format - Format of the day name ('short' or 'long')
 * @returns Day name string
 */
export const getDayName = (date: Date | string, format: 'short' | 'long' = 'short'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', { weekday: format });
};

/**
 * Check if two dates are the same day (ignoring time)
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Boolean indicating if dates are the same day
 */
export const isSameDay = (date1: Date | string, date2: Date | string): boolean => {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
  
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

/**
 * Check if a date is today
 * @param date - Date to check
 * @returns Boolean indicating if date is today
 */
export const isToday = (date: Date | string): boolean => {
  const dateToCheck = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  
  return (
    dateToCheck.getDate() === today.getDate() &&
    dateToCheck.getMonth() === today.getMonth() &&
    dateToCheck.getFullYear() === today.getFullYear()
  );
};

/**
 * Format a number as currency with 2 decimal places
 * @param value - Number to format
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number): string => {
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
